<template>
  <div class="volume-picker-page">
    <div class="container-fluid mt-5 mb-5 px-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h2>New Transaction</h2>
        <div class="d-flex align-items-center gap-3">
          <NewSaleLinksComponent current="forward" />
          <!-- Creating a deal is the whole page's action, not one section's, so it sits with Cancel
               at the top right as the sketch draws it (FIX4-1 item 5a). -->
          <router-link
            to="/admin/transactions"
            class="btn btn-outline-secondary"
          >
            {{ $t("forwards.picker.cancel") }}
          </router-link>
          <ActionButtonWithSpinner
            :busy="creating"
            :disabled="!canConfirm"
            variant="btn-primary"
            size="md"
            data-testid="picker-confirm"
            @click="startConfirm"
          >
            {{
              creating
                ? $t("forwards.picker.confirming")
                : $t("forwards.picker.confirm")
            }}
          </ActionButtonWithSpinner>
        </div>
      </div>

      <p
        v-if="confirmBlockedReason"
        class="text-body-tertiary fs-10 text-end mb-3"
        data-testid="picker-confirm-blocked"
      >
        {{ confirmBlockedReason }}
      </p>

      <div v-if="createError" class="error-note mb-3" role="alert">
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ createError }}</span>
      </div>

      <PickerDealCardComponent v-model="identity" :showNameError />

      <!-- The card header carries what the card is and what it binds; inside it there is nothing but
         the three control rows, so the body opens on the first control (FIX4-2 points 3 and 4). -->
      <div class="card mb-3" data-testid="picker-filters-card">
        <div class="card-header bg-light">
          <h5 class="mb-0">{{ $t("forwards.picker.filters.title") }}</h5>
          <p class="text-body-tertiary fs-10 mb-0 mt-1">
            {{ $t("forwards.picker.criteria.description") }}
          </p>
        </div>
        <div class="card-body">
          <PickerProductionRangeComponent v-model="period" />
          <PickerCriteriaFiltersComponent
            v-model="criteria"
            v-model:minPrice="minPriceFilter"
          />
        </div>
      </div>

      <h3 class="mb-3">{{ $t("forwards.picker.pools.title") }}</h3>

      <div class="row g-3">
        <div class="col-12 col-lg picker-take-column">
          <PickerTakeTableComponent
            v-model="takes"
            :rows="rows"
            :ineligible="pools?.ineligible ?? []"
            :productWarnings="productWarnings"
            :hint="hint"
            :loading="loading"
            :error="loadError"
            @retry="loadPools"
            @update:rowsInScope="rowsInScope = $event"
          />
        </div>
        <div class="col-12 col-lg-auto">
          <PickerSummaryColumnComponent
            :rows="rows"
            :rowsInScope="rowsInScope"
            :takes="takes"
            :qualificationDate="pools?.qualification_date ?? null"
          />
        </div>
      </div>

      <div class="card mt-3">
        <div class="card-body py-2 text-body-tertiary fs-10">
          {{ $t("forwards.picker.footer_note") }}
        </div>
      </div>
    </div>

    <!-- A pool edited from the take table's pool links can change what it lists and releases. -->
    <PoolEditorModalHost @written="loadPools" />
  </div>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import { forwardsApi, forwardsErrorMessage } from "@/forwards2/forwardsApi";
import { isForwardsStaff } from "@/forwards2/forwardsGuard";
import type {
  MinimumPriceHint,
  PickerConfirmRequest,
  PickerPoolRow,
  PickerPools,
  PickerQuery,
} from "@/forwards2/forwardsTypes";
import type { TakeMap } from "@/forwards2/picker/distributeTakes";
import NewSaleLinksComponent from "@/forwards2/picker/NewSaleLinksComponent.vue";
import PickerCriteriaFiltersComponent from "@/forwards2/picker/PickerCriteriaFiltersComponent.vue";
import PickerDealCardComponent from "@/forwards2/picker/PickerDealCardComponent.vue";
import PickerProductionRangeComponent from "@/forwards2/picker/PickerProductionRangeComponent.vue";
import PickerSummaryColumnComponent from "@/forwards2/picker/PickerSummaryColumnComponent.vue";
import PickerTakeTableComponent from "@/forwards2/picker/PickerTakeTableComponent.vue";
import PoolEditorModalHost from "@/forwards2/pools/PoolEditorModalHost.vue";
import {
  confirmRequest,
  type DealIdentityForm,
  defaultPeriodForm,
  type EligibilityCriteriaForm,
  emptyCriteriaForm,
  emptyDealIdentityForm,
  periodRange,
  pickerQuery,
  type PickerPeriodForm,
} from "@/forwards2/picker/pickerForm";
import { debounce, pickBy } from "lodash-es";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/**
 * The volume picker: a forward deal from the product definition to the takes that back it.
 *
 * One page, read top to bottom in the layout of the spot sale page it sits beside (FIX4-1 item 5,
 * sketch 1d): the deal's identity where the spot flow has its buyer card, the range and criteria
 * where it has its filter panel, and the take table beside its summary column where it has the
 * portfolio-item table beside its breakdowns. It shares no state with the transaction form (plan
 * Appendix D row 1d): a forward deal is not a spot sale with different inventory, and its buyer,
 * prices and delivery plan are set on the deal page afterwards. Its route admits staff only (the
 * `/admin` guard). A sales admin opens it read-only (user decision, 2026-09-28): every read
 * is its to make, but the confirm is `SUPER`, so the button is disabled with the reason and the
 * confirm path never sends it.
 *
 * The take table follows the form the way the spot table follows its filters — debounced, from the
 * first render onward. Its *rows* are a function of the query alone — the criteria and the range,
 * never the deal's name — so only a changed query re-asks for them; the set of taken pools re-asks
 * for the selection's advice instead, the price hint and the product warnings, which describe which
 * pools are selected rather than how much each gives. Taking volume
 * therefore never replaces the rows under the trader's caret (FIX4-4 point 4), and volume alone
 * changes nothing at all.
 *
 * Each pool's take is capped at what its sales strategy releases today, and the answer leaves out a
 * pool whose strategy has released none of its volume yet, with the date more is released. There is
 * no override here: volume past a strategy is backed slice by slice on the deal page, where the back
 * and resize dialogs offer it to a super admin.
 */

const { t } = useI18n();
const router = useRouter();
const { globalErrorHandler } = useGlobalHandlers();

const REFETCH_DEBOUNCE_MS = 400;

let poolRequestGeneration = 0;
let adviceRequestGeneration = 0;
let acceptedPoolsGeneration = 0;

const identity = ref<DealIdentityForm>(emptyDealIdentityForm());
const criteria = ref<EligibilityCriteriaForm>(emptyCriteriaForm());
/** Narrows the rows on screen; never part of the created deal. */
const minPriceFilter = ref<number | null>(null);
const period = ref<PickerPeriodForm>(defaultPeriodForm());
const takes = ref<TakeMap>({});

const pools = ref<PickerPools | null>(null);
const acceptedPickerQuery = ref<PickerQuery | null>(null);
const hint = ref<MinimumPriceHint | null>(null);
const productWarnings = ref<string[]>([]);
const loading = ref(false);
const loadError = ref<unknown>(null);
const creating = ref(false);
const createError = ref<string | null>(null);

/** Set when the pool query changes: the next refresh re-queries the rows instead of the advice. */
const rowsOutdated = ref(true);

// The route guard loaded the account before this page opened.
const accountStore = useAccountStore();
const canCreateDeals = computed(() => isForwardsStaff(accountStore.account));

const periods = computed(() => periodRange(period.value));
const rows = computed(() => pools.value?.rows ?? []);

/**
 * The rows the take table's header filters leave on screen. The table owns those filters, so the
 * summary column beside it can only describe the same set by being told what it is.
 */
const rowsInScope = ref<PickerPoolRow[]>([]);

const takenPoolIds = computed(() =>
  Object.entries(takes.value)
    .filter(([, volume]) => volume > 0)
    .map(([poolId]) => Number(poolId))
    .sort((left, right) => left - right)
);

/** Stable identity for "which pools are selected" — the only take change worth a refetch. */
const takenPoolKey = computed(() => takenPoolIds.value.join(","));

/** Stable identity for "which pools can serve this", i.e. the request minus the selection. */
const poolQueryKey = computed(() =>
  JSON.stringify(
    pickerQuery(criteria.value, minPriceFilter.value, periods.value, [])
  )
);

/**
 * A take above what its pool's strategy has released. The input already marks it invalid and the
 * backend would refuse the deal, so the confirm waits for it to be lowered.
 */
const takeOverCapacity = computed(() =>
  rows.value.some((row) => (takes.value[row.pool_id] ?? 0) > row.capacity_total)
);

/**
 * Why the deal cannot be created yet, or null when it can — the sentence beside the disabled button.
 *
 * A confirm needs an answer on screen. Without one — the load failed, so the rows are gone and the
 * takes were never pruned — confirming would create commitments from pools nothing is rendering,
 * against the form's range rather than the answer's (RV2-deal-creation-01). Nor may it act on the
 * answer to an earlier form: from a criteria or range change until the new answer lands, the rows
 * on screen and the confirm request still describe the previous product.
 */
const confirmBlockedReason = computed(() => {
  if (!canCreateDeals.value)
    return t("forwards.common.super_admin_only_create");
  if (identity.value.name.trim().length === 0)
    return t("forwards.picker.criteria.name_required");
  if (periods.value.length === 0)
    return t("forwards.picker.period.invalid_range");
  if (rowsOutdated.value || loading.value)
    return t("forwards.picker.take.refresh_pending");
  if (pools.value === null) return t("forwards.picker.take.load_failed");
  if (takenPoolIds.value.length === 0)
    return t("forwards.picker.errors.no_takes");
  if (takeOverCapacity.value) return t("forwards.picker.errors.over_capacity");
  return null;
});

const canConfirm = computed(() => confirmBlockedReason.value === null);

/** The name is the one field nothing else on the page explains, so it is marked once it is all that
 *  stands between the trader and a deal. */
const showNameError = computed(
  () => identity.value.name.trim().length === 0 && takenPoolIds.value.length > 0
);

/** Drops takes on pools this answer no longer offers, so nothing invisible reaches the confirm (RV-picker-02). */
const pruneTakes = (loaded: PickerPools) => {
  const offered = new Set(loaded.rows.map((row) => row.pool_id));
  const kept = pickBy(takes.value, (volume, poolId) =>
    offered.has(Number(poolId))
  );
  if (Object.keys(kept).length !== Object.keys(takes.value).length) {
    takes.value = kept;
  }
};

const currentConfirmRequest = (): PickerConfirmRequest | null => {
  const answered = pools.value;
  const acceptedQuery = acceptedPickerQuery.value;
  if (answered === null || acceptedQuery === null) return null;

  return confirmRequest(
    identity.value,
    acceptedQuery.criteria,
    answered.periods,
    takes.value
  );
};

const loadPools = async () => {
  const requestedPeriods = [...periods.value];
  if (requestedPeriods.length === 0) return;

  const requestGeneration = ++poolRequestGeneration;
  const requestKey = poolQueryKey.value;
  const query = pickerQuery(
    criteria.value,
    minPriceFilter.value,
    requestedPeriods,
    takenPoolIds.value
  );

  loading.value = true;
  try {
    const response = await forwardsApi.getPickerPools(query);
    if (
      requestGeneration !== poolRequestGeneration ||
      requestKey !== poolQueryKey.value
    )
      return;

    pools.value = response.data;
    acceptedPickerQuery.value = query;
    acceptedPoolsGeneration += 1;
    loadError.value = null;
    pruneTakes(response.data);
    if (query.taken_pool_ids.join(",") === takenPoolKey.value) {
      applyAdvice(response.data);
    } else {
      void loadSelectionAdvice();
    }
  } catch (error) {
    if (
      requestGeneration !== poolRequestGeneration ||
      requestKey !== poolQueryKey.value
    )
      return;

    loadError.value = error;
    pools.value = null;
    acceptedPickerQuery.value = null;
    acceptedPoolsGeneration += 1;
    adviceRequestGeneration += 1;
    // The advice describes an answer that is now gone; leaving it up would price a table nothing
    // is rendering.
    hint.value = null;
    productWarnings.value = [];
  } finally {
    if (
      requestGeneration === poolRequestGeneration &&
      requestKey === poolQueryKey.value
    )
      loading.value = false;
  }
};

const applyAdvice = (loaded: PickerPools) => {
  hint.value = loaded.hint;
  productWarnings.value = loaded.product_warnings;
};

/**
 * Re-asks the advice about the pools now taken, and nothing else.
 *
 * `deal_picker_service.qualifying_pools` reads `taken_pool_ids` only to choose the pools the hint
 * and the warnings describe (`_selected_pools`) — the rows themselves depend on the criteria and the
 * range — so the answer's rows are dropped on purpose here. Leaving `pools` alone is what keeps a
 * keystroke from rebuilding the table, and the caret in it (FIX4-4 point 4).
 */
const loadSelectionAdvice = async () => {
  const answered = pools.value;
  const acceptedQuery = acceptedPickerQuery.value;
  if (answered === null || acceptedQuery === null) return;

  const requestGeneration = ++adviceRequestGeneration;
  const poolsGeneration = acceptedPoolsGeneration;
  const selectionKey = takenPoolKey.value;
  const query: PickerQuery = {
    ...acceptedQuery,
    periods: answered.periods,
    taken_pool_ids: takenPoolIds.value,
  };

  try {
    const response = await forwardsApi.getPickerPools(query);
    if (
      requestGeneration !== adviceRequestGeneration ||
      poolsGeneration !== acceptedPoolsGeneration ||
      selectionKey !== takenPoolKey.value
    )
      return;
    applyAdvice(response.data);
  } catch (error) {
    if (
      requestGeneration !== adviceRequestGeneration ||
      poolsGeneration !== acceptedPoolsGeneration ||
      selectionKey !== takenPoolKey.value
    )
      return;
    // Advisory: the table on screen stays usable and the last advice stands, but a failure is still a
    // real fault.
    globalErrorHandler(error);
  }
};

const refresh = () => {
  if (rowsOutdated.value || pools.value === null) {
    rowsOutdated.value = false;
    void loadPools();
  } else {
    void loadSelectionAdvice();
  }
};

const debouncedRefresh = debounce(refresh, REFETCH_DEBOUNCE_MS);

const startConfirm = () => {
  createError.value = null;
  if (!canConfirm.value) {
    createError.value = confirmBlockedReason.value;
    return;
  }
  const request = currentConfirmRequest();
  if (request !== null) void createDeal(request);
};

const createDeal = async (request: PickerConfirmRequest) => {
  if (!canCreateDeals.value) return;
  creating.value = true;
  createError.value = null;
  try {
    const response = await forwardsApi.createDeal(request);
    await router.push(dealRoute(response.data.id));
  } catch (error) {
    createError.value =
      forwardsErrorMessage(error) ?? t("forwards.picker.errors.create_failed");
  } finally {
    creating.value = false;
  }
};

watch(poolQueryKey, () => {
  poolRequestGeneration += 1;
  loading.value = false;
  rowsOutdated.value = true;
  debouncedRefresh();
});

watch(takenPoolKey, () => {
  hint.value = null;
  productWarnings.value = [];
  adviceRequestGeneration += 1;
  debouncedRefresh();
});

onMounted(refresh);

// A request still in flight after navigation must not apply its answer, start follow-up work, or
// report its failure on the page the user moved to.
onUnmounted(() => {
  debouncedRefresh.cancel();
  poolRequestGeneration += 1;
  adviceRequestGeneration += 1;
});
</script>

<style lang="scss" scoped>
.volume-picker-page {
  flex-grow: 1;
  text-align: left;
  overflow-y: auto;
  max-width: 100%;
}

// A flex item never shrinks below its content's width, and the take table's period columns are
// wider than the space beside the summary, so without this the summary wraps below the table.
.picker-take-column {
  min-width: 0;
}
</style>
